import i18n from 'i18next';
import { LoaderCircle } from 'lucide-react';

import type { buttonVariants } from '@/lib/button-variants';
import type { VariantProps } from 'class-variance-authority';
import type { FC, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

type ButtonVariant = VariantProps<typeof buttonVariants>['variant'];

interface ModalProps {
  triggerer?: ReactNode;
  title: string;
  children: ReactNode;
  rejectButtonLabel?: string;
  acceptButtonLabel?: string;
  acceptButtonVariant?: ButtonVariant;
  rejectButtonVariant?: ButtonVariant;
  className?: string;
  onConfirm?: () => void;
  showCloseButton?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
}

const Modal: FC<ModalProps> = ({
  triggerer,
  title,
  children,
  className,
  rejectButtonLabel = i18n.t('actions.cancel'),
  acceptButtonLabel = 'Delete',
  acceptButtonVariant = 'default',
  rejectButtonVariant,
  onConfirm,
  showCloseButton = false,
  open,
  onOpenChange,
  isLoading,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {triggerer && (
        <DialogTrigger className={cn('size-8', className)} asChild>
          <Button variant="outline">{triggerer}</Button>
        </DialogTrigger>
      )}
      <DialogContent
        showCloseButton={showCloseButton}
        className="flex max-h-[min(600px,80vh)] flex-col gap-4 p-6 md:min-w-120 sm:max-w-md"
      >
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <LoaderCircle className="size-12 animate-spin text-primary h-28.5" />
          </div>
        ) : (
          <ScrollArea className="overflow-hidden">
            <div className="flex flex-col gap-4 overflow-hidden">
              <DialogHeader className="contents space-y-0 text-left text-xl font-semibold">
                <DialogTitle className="leading-6.5">{title}</DialogTitle>
              </DialogHeader>
              <DialogDescription className="h-full">{children}</DialogDescription>
              <DialogFooter className="m-0 sm:justify-end">
                <DialogClose asChild>
                  <Button variant={rejectButtonVariant}>{rejectButtonLabel}</Button>
                </DialogClose>
                <Button type="button" variant={acceptButtonVariant} onClick={onConfirm}>
                  {acceptButtonLabel}
                </Button>
              </DialogFooter>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
